import React, { useCallback, useEffect, useState } from 'react';
import { formatPhone } from '../../../../helpers/phoneUtils';
import { useParams, useHistory } from 'react-router-dom';
import withTheme from '@material-ui/styles/withTheme';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    IconButton
} from '@material-ui/core';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import { userGroupAutocomplete } from '../../../../autocomplete';
import FormSection from '../../../../components/atom/FormSection';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { ErrorIcon } from '../../../../icons';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CapabilityEditor from '../../../../components/atom/CapabilityEditor';
import { useDispatch } from 'react-redux';
import { refreshUserCapabilities } from '../../../../actions/user';
import { EditIcon } from '../../../../icons';

function ClientUserEditPage({
    clientId,
    theme,
    openUserEditModal,
    user,
    loadingUser,
    errorUser,
    setUser
}) {
    const { userId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();

    const [capabilities, setCapabilities] = useState(user?.guest?.capabilities || []);
    const dispatch = useDispatch();

    const roles = [
        { id: 1, name: 'customer' },
        { id: 2, name: 'agent' },
        { id: 3, name: 'admin' }
    ];
    const [userGroups, setUserGroups] = useState(false);
    const [selectedRole, setSelectedRole] = useState('customer');
    const [agentStictLimit, setAgentStrictLimit] = useState(false);
    useEffect(() => {
        if (user?.guest?.role) {
            setSelectedRole(user?.guest?.role);
            setAgentStrictLimit(user?.guest?.agent_strict_limit);
            return;
        }
        setSelectedRole(selectedRole);
    }, [user, setSelectedRole]);

    useEffect(() => {
        setUserGroups(user?.guest?.user_groups ? user?.guest?.user_groups.map(e => e.id) : []);
    }, [user, setUserGroups]);

    // switch user's role
    const changeUserRole = useCallback(
        ({ role, agent_strict_limit }) => {
            doSwaggerCall('users', 'switchRole', { userId, newRole: { role, agent_strict_limit } })
                .then(() => {
                    // we finished, change role
                    notifySuccess('User role and limitation changed!');
                    history.push(`/admin/client/${clientId}/users`);
                })
                .catch(() => {
                    notifyError('Cannot change user role!');
                });
        },
        [notifyError, notifySuccess]
    );
    // modify user's userGroups
    const modifyUsersUserGroup = () => {
        doSwaggerCall('users', 'updateUsersUserGroups', {
            userId,
            userGroupIds: userGroups
        })
            .then(newUser => {
                setUser(newUser);
                notifySuccess('Users userGroups modified!');
                history.push(`/admin/client/${clientId}/users`);
            })
            .catch(err => {
                console.log(err);
                notifyError('Could not modify users userGroups!');
            });
    };

    const modifyCapabilities = () => {
        doSwaggerCall('users', 'updateUser', {
            userId,
            user: {
                client_id: parseInt(clientId, 10),
                capabilities: capabilities
            }
        })
            .then(() => {
                history.push(`/admin/client/${clientId}/users`);
                dispatch(refreshUserCapabilities());
            })
            .catch(err => {
                console.log(err);
                notifyError('Could not modify user capabilities!');
            });
    };

    if (loadingUser || userGroups === false) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <form
            onSubmit={ev => {
                ev.preventDefault();
            }}
        >
            <Grid container spacing={2}>
                <AdminHeader title="User edit" />
                {!!errorUser && (
                    <Grid
                        item
                        container
                        spacing={2}
                        xs={12}
                        style={{
                            color: theme.palette.error.main
                        }}
                        alignItems="center"
                    >
                        <Grid item>
                            <ErrorIcon color="error" />
                        </Grid>
                        <Grid item>
                            <span>{errorUser.response.body.message}</span>
                        </Grid>
                    </Grid>
                )}
                <FormSection title="Generic informations">
                    <Grid container item spacing={2}>
                        <Grid item xs={10}>
                            <h5>Email:</h5>
                            {user.guest?.email}
                        </Grid>
                        <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton onClick={openUserEditModal}>
                                <EditIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                            <h5>First Name:</h5>
                            {user.guest?.firstname}
                        </Grid>
                        <Grid item xs={12}>
                            <h5>Last Name:</h5>
                            {user.guest?.lastname}
                        </Grid>
                        <Grid item xs={12}>
                            <h5>Phone Number:</h5>
                            {formatPhone(user.guest?.phone)}
                        </Grid>
                        {user?.guest?.arrival_date && (
                            <Grid item xs={12}>
                                <h5>Arrival date:</h5>
                                {user.guest.arrival_date}
                            </Grid>
                        )}
                        {user?.guest?.departure_date && (
                            <Grid item xs={12}>
                                <h5>Departure date:</h5>
                                {user.guest?.departure_date}
                            </Grid>
                        )}
                        {user?.guest?.guest_field_info?.map(field => {
                            if (
                                field.value === '' ||
                                field.value === null ||
                                typeof field.value === 'undefined'
                            ) {
                                return null;
                            }
                            if (typeof field.value === 'boolean') {
                                return (
                                    <Grid item xs={12} key={`field-${field.id}`}>
                                        <h5>{field.name}</h5>
                                        <h4>{field.value.toString()}</h4>
                                    </Grid>
                                );
                            }
                            return (
                                <Grid item xs={12} key={`field-${field.id}`}>
                                    <h5>{field.name}</h5>
                                    <h4>{field.value}</h4>
                                </Grid>
                            );
                        })}
                    </Grid>
                </FormSection>
                <FormSection title="Role and User Group settings">
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel id="role">Role</InputLabel>
                            <Select
                                labelId="role"
                                fullWidth
                                value={selectedRole}
                                onChange={event => setSelectedRole(event.target.value)}
                            >
                                {roles.map(r => (
                                    <MenuItem key={r.id} value={r.name}>
                                        {r.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <AutocompleteSelect
                            initCall={userGroupAutocomplete.init}
                            autocompleteCall={(search, pageSize) => {
                                return doSwaggerCall('usergroups', 'listUserGroupsPerClient', {
                                    clientId,
                                    pageSize,
                                    search
                                }).then(({ userGroups: uGroups }) =>
                                    uGroups.map(e => {
                                        return { id: e.id, name: `${e.name}` };
                                    })
                                );
                            }}
                            value={userGroups}
                            multiple
                            setValue={value => {
                                if (value === null) {
                                    setUserGroups([]);
                                    return;
                                }
                                setUserGroups(value);
                            }}
                        >
                            <TextField label="User Group" />
                        </AutocompleteSelect>
                    </Grid>
                    {selectedRole === 'agent' && (
                        <Grid xs={12} item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agentStictLimit}
                                        onChange={e => {
                                            setAgentStrictLimit(e.target.checked);
                                        }}
                                    />
                                }
                                label="This agent can only see bookings and guests they created"
                            />
                        </Grid>
                    )}
                    <CapabilityEditor
                        value={user?.guest?.capabilities}
                        onChange={newCapabilities => setCapabilities(newCapabilities)}
                    />
                </FormSection>
                <Grid item xs={6}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (
                                user.guest?.role !== selectedRole ||
                                user.guest?.agent_strict_limit !== selectedRole.agent_strict_limit
                            ) {
                                changeUserRole({
                                    role: selectedRole,
                                    agent_strict_limit: agentStictLimit
                                });
                            }
                            modifyUsersUserGroup();
                            modifyCapabilities();
                        }}
                    >
                        Submit
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => history.push(`/admin/client/${clientId}/users`)}
                    >
                        Cancel
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
}

export default withTheme(ClientUserEditPage);
